import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Anchor, Badge, Box, Button, Group, Progress, Stack, Stepper, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ArrowLeft, ArrowRight, RefreshCw, Sparkles } from 'lucide-react';
import PageHeaderComponent from '@/design/PageHeaderComponent';
import { SurfaceCard } from '@/design/SurfaceCard';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useAuth } from '@/features/auth/AuthContext';
import { resolveDefaultRoute } from '@/shell/SidebarNav';
import { isTerminalJob } from '@/features/notifications/NotificationTypes';
import { OnboardingViewModel, type TrackedRow } from './OnboardingViewModel';
import { OnboardingConnectStep } from './steps/OnboardingConnectStep';
import { OnboardingTeamStep } from './steps/OnboardingTeamStep';
import { OnboardingPaymentStep } from './steps/OnboardingPaymentStep';

interface WizardStep {
    key: 'provider' | 'identity' | 'team' | 'payment' | 'finish';
    label: string;
    optional: boolean;
}

export default function OnboardingPage(): ReactElement {
    const vm = useViewModel(OnboardingViewModel);
    const { user, hasFeature, hasPermission, hasRole, reloadUser } = useAuth();
    const navigate = useNavigate();
    const finished = useRef(false);
    const defaultRoute = resolveDefaultRoute(hasFeature, hasPermission, hasRole);

    const [active, setActive] = useState(0);
    const [providerConnId, setProviderConnId] = useState<string | null>(null);
    const [directoryConnId, setDirectoryConnId] = useState<string | null>(null);
    const [finishing, setFinishing] = useState(false);
    const pipelineStarted = useRef(false);

    useEffect(() => { void vm.load(); }, [vm]);

    const steps = useMemo<WizardStep[]>(() => {
        const list: WizardStep[] = [
            { key: 'provider', label: 'Provider', optional: false },
            { key: 'identity', label: 'Identity', optional: true },
            { key: 'team', label: 'Team', optional: true },
        ];
        if (vm.isSaas) list.push({ key: 'payment', label: 'Payment', optional: true });
        list.push({ key: 'finish', label: 'Finish', optional: false });
        return list;
    }, [vm.isSaas]);

    const current = steps[active] ?? steps[0]!;
    const finishIndex = steps.length - 1;
    const isLast = active === finishIndex;

    useEffect(() => {
        if (current.key === 'finish' && !pipelineStarted.current) {
            pipelineStarted.current = true;
            void vm.startPipeline({
                ...(providerConnId ? { providerConnectionId: providerConnId } : {}),
                ...(directoryConnId ? { directoryConnectionId: directoryConnId } : {}),
                runInsights: hasFeature('insights'),
            });
        }
    }, [current.key, providerConnId, directoryConnId, hasFeature, vm]);

    if (!finished.current && user?.onboarding && !user.onboarding.needsOnboarding) {
        return <Navigate to={defaultRoute} replace />;
    }

    const goNext = (): void => setActive((a) => Math.min(finishIndex, a + 1));
    const goBack = (): void => setActive((a) => Math.max(0, a - 1));

    const skipAll = async (): Promise<void> => {
        try { await vm.dismiss(); await reloadUser(); } catch { void 0; }
        navigate(defaultRoute);
    };

    const finish = async (): Promise<void> => {
        finished.current = true;
        setFinishing(true);
        try { await vm.complete(); await reloadUser(); } catch (e) {
            notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Failed to finish' });
        }
        navigate(defaultRoute);
    };

    const nextDisabled = current.key === 'provider' && !providerConnId;

    return (
        <Box>
            <Group justify="space-between" align="flex-start">
                <PageHeaderComponent title="Welcome to Ultra Trace" subtitle="Set up your workspace in a few steps." />
                <Anchor size="sm" c="dimmed" onClick={() => void skipAll()} style={{ whiteSpace: 'nowrap', marginTop: 6 }}>Skip setup</Anchor>
            </Group>

            <Stepper active={active} onStepClick={(i) => setActive(i)} size="sm" mt="md" mb="xl" allowNextStepsSelect={false}>
                {steps.map((s) => <Stepper.Step key={s.key} label={s.label} />)}
            </Stepper>

            <Box mih={220} mb="xl">
                {current.key === 'provider' && <OnboardingConnectStep kind="ai" onConnectedChange={setProviderConnId} />}
                {current.key === 'identity' && <OnboardingConnectStep kind="directory" onConnectedChange={setDirectoryConnId} />}
                {current.key === 'team' && <OnboardingTeamStep onInvited={goNext} />}
                {current.key === 'payment' && <OnboardingPaymentStep onSaved={goNext} />}
                {current.key === 'finish' && <FinishStep vm={vm} finishing={finishing} onFinish={() => void finish()} />}
            </Box>

            {!isLast && (
                <Group justify="space-between">
                    <Button variant="default" leftSection={<ArrowLeft size={16} />} onClick={goBack} disabled={active === 0}>Back</Button>
                    <Button
                        rightSection={<ArrowRight size={16} />}
                        onClick={goNext}
                        disabled={nextDisabled}
                        variant={current.optional && !isStepSatisfied(current, providerConnId, directoryConnId) ? 'default' : 'filled'}
                    >
                        {current.optional && !isStepSatisfied(current, providerConnId, directoryConnId) ? 'Skip' : 'Continue'}
                    </Button>
                </Group>
            )}
        </Box>
    );
}

function isStepSatisfied(step: WizardStep, providerConnId: string | null, directoryConnId: string | null): boolean {
    if (step.key === 'identity') return !!directoryConnId;
    if (step.key === 'provider') return !!providerConnId;
    return false;
}

const STATUS_META: Record<string, { color: string; label: string }> = {
    running: { color: 'brand', label: 'In progress' },
    pending: { color: 'zinc', label: 'Waiting' },
    succeeded: { color: 'green', label: 'Done' },
    partial: { color: 'yellow', label: 'Finishing up' },
    failed: { color: 'red', label: 'Failed' },
    canceled: { color: 'zinc', label: 'Canceled' },
};

function FinishStep({ vm, finishing, onFinish }: { vm: OnboardingViewModel; finishing: boolean; onFinish: () => void }): ReactElement {
    return (
        <Stack gap="md" maw={520} mx="auto">
            <SurfaceCard>
                <Group gap="xs" mb="xs"><Sparkles size={16} /><Text size="sm" fw={600}>Setting up your workspace</Text></Group>
                <Text size="xs" c="dimmed" mb="md">We&apos;re pulling your data and building your insights. Follow along here or in the notification tray — this can take a few minutes.</Text>
                <Stack gap="sm">
                    {vm.rows.length === 0
                        ? <Text size="sm" c="dimmed">Starting…</Text>
                        : vm.rows.map((row) => <JobRow key={row.kind} row={row} />)}
                </Stack>
            </SurfaceCard>
            <Group justify="space-between">
                {vm.anyFailed
                    ? <Button variant="light" color="orange" leftSection={<RefreshCw size={15} />} onClick={() => void vm.retry()}>Retry</Button>
                    : <span />}
                <Button rightSection={<ArrowRight size={16} />} loading={finishing} disabled={!vm.allComplete} onClick={onFinish}>
                    Go to your workspace
                </Button>
            </Group>
        </Stack>
    );
}

function JobRow({ row }: { row: TrackedRow }): ReactElement {
    const meta = STATUS_META[row.status] ?? STATUS_META['running']!;
    const terminal = row.status !== 'pending' && isTerminalJob(row.status);
    const determinate = row.status === 'running' && typeof row.total === 'number' && row.total > 0;
    const pct = determinate ? Math.min(100, Math.round(((row.progress ?? 0) / (row.total ?? 1)) * 100)) : 100;
    return (
        <Box>
            <Group justify="space-between" mb={4}>
                <Text size="sm">{row.label}</Text>
                <Badge size="xs" variant="light" color={meta.color}>{meta.label}</Badge>
            </Group>
            <Progress value={pct} striped={!terminal && !determinate} animated={!terminal && !determinate} size="sm" color={meta.color} />
            {row.message && <Text size="xs" c="dimmed" mt={2}>{row.message}</Text>}
        </Box>
    );
}
